import { FpjsClient } from '@fingerprintjs/fingerprintjs-pro-spa'
import { action, computed, observable, runInAction } from 'mobx'
import { clearPersistedStore, makePersistable } from 'mobx-persist-store'
import { FPJS_CONFIG } from '@/constants/fpjs.js'

export default class FingerprintStore {
  rootStore = null

  #isBot = false
  #hasMfa = false
  #hasSfa = false
  #isFirstVisit = false

  // TODO: would be more secure for FE to only know the requestId, and BE gets everything else
  // TODO: since we store the fingerprint indefinitely, there's a risk someone could steal a user's
  //  fingerprint and use it. We should look into this
  @observable accessor isInitialized = false
  @observable accessor requestId = ''
  @observable accessor visitorId = ''
  @observable accessor firstSeenAt = ''
  @observable accessor hasAcceptedLegalDocs = false
  @observable accessor device = 'desktop'
  @observable accessor os = 'windows'

  constructor(rootStore) {
    this.rootStore = rootStore
    this.rootStore.registerAfterConstructor(this.#_afterConstructor)
  }

  #_afterConstructor = async () => {
    await makePersistable(this, {
      name: 'fpjs',
      properties: ['requestId', 'visitorId', 'firstSeenAt', 'hasAcceptedLegalDocs', 'device', 'os'],
    })
    this.#migrate()
  }

  init = async () => {
    if (this.isInitialized) return

    if (!this.hasFingerprint) await this.#generate()
    else console.log('[Fingerprint] Fingerprint detected')

    const response = await this.api.sendEvent('locationsearch', {
      hash: location.search || location.origin + location.pathname,
    })

    if (response?.data?.status !== 'OK') {
      if (response?.data?.code === 404 || response?.status === 404) {
        // in case of failed migration, generate a new fingerprint
        await clearPersistedStore(this)
        await this.#generate(true)

        this.router.reload()

        return
      }

      throw new Error('[Fingerprint] Error retrieving user info')
    }

    this.#isBot = Boolean(response.data.isBot)
    this.#hasMfa = Boolean(response.data.MFA)
    this.#hasSfa = Boolean(response.data.SFA)
    this.#isFirstVisit = Boolean(response.data.firstTime)

    if (response.data.invitation_viewed > 0) await this.analytics.invitationViewed()

    runInAction(() => {
      this.hasAcceptedLegalDocs ||= Boolean(response.data.exist)
      this.isInitialized = true
    })

    if (this.hasAcceptedLegalDocs) this.analytics.userReturn()?.then()
    // this.analytics.apiEventReferer()?.then()
  }

  get analytics() {
    return this.rootStore.analyticsStore
  }

  get api() {
    return this.rootStore.apiStore
  }

  get router() {
    return this.rootStore.routerStore
  }

  @computed
  get hasFingerprint() {
    return Boolean(this.requestId) && Boolean(this.visitorId) && Boolean(this.firstSeenAt)
  }

  get isBot() {
    return this.#isBot
  }

  get hasMfa() {
    return this.#hasMfa
  }

  get hasSfa() {
    return this.#hasSfa
  }

  get isFirstVisit() {
    return this.#isFirstVisit
  }

  @computed
  get mustAcceptLegalDocs() {
    return !this.hasAcceptedLegalDocs && !this.isBot
  }

  @computed
  get isDesktop() {
    return this.device === 'desktop'
  }

  @computed
  get isMobile() {
    return this.device === 'mobile'
  }

  @computed
  get isWindows() {
    return this.os === 'windows'
  }

  @computed
  get isMac() {
    return this.os === 'macos'
  }

  @computed
  get isLinux() {
    return this.os === 'linux'
  }

  @computed
  get isIOS() {
    return this.os === 'ios'
  }

  @computed
  get isAndroid() {
    return this.os === 'android'
  }

  @computed
  get isOtherOS() {
    return !this.isWindows && !this.isMac && !this.isLinux && !this.isIOS && !this.isAndroid
  }

  // used manually in the dev console
  @action
  createFakeTestUser = async () => {
    if (!import.meta.env.DEV) return

    console.log('[Fingerprint] Generating test user fingerprint...')
    runInAction(() => {
      this.requestId = crypto.randomUUID()
      this.visitorId = crypto.randomUUID()
      this.firstSeenAt = '1977-12-31T02:30:00.000Z'
    })
    await this.router.navigate(null, { reloadDocument: true })
  }

  // used manually in the dev console
  // noinspection JSUnusedGlobalSymbols
  resetFingerprint = async () => {
    if (!import.meta.env.DEV) return

    console.log('[Fingerprint] Resetting...')
    await clearPersistedStore(this)
    await this.router.navigate(null, { reloadDocument: true })
  }

  @action
  acceptLegalDocs = () => {
    this.hasAcceptedLegalDocs = true
    this.analytics.acceptTermsAndConditions()?.then()
  }

  @action
  #generate = async (ignoreCache = false) => {
    console.log('[Fingerprint] Generating...')
    const fpjsClient = new FpjsClient(FPJS_CONFIG)
    await fpjsClient.init()
    const data = await fpjsClient.getVisitorData({ extendedResult: true, ignoreCache })
    console.log('[Fingerprint] Success', data)

    runInAction(() => {
      this.requestId = data.requestId
      this.visitorId = data.visitorId
      this.firstSeenAt = data.firstSeenAt.global
      this.device = data.device.toLowerCase() === 'other' ? 'desktop' : 'mobile'
      this.os = data.os.toLowerCase().replaceAll(' ', '')
    })
  }

  @action
  #migrate = () => {
    if (!this.hasFingerprint) {
      const requestId = localStorage.getItem('requestId')
      const visitorId = localStorage.getItem('visitorId')
      const firstSeenAt = localStorage.getItem('firstSeenAt')

      if (requestId && visitorId && firstSeenAt) {
        this.requestId = requestId
        this.visitorId = visitorId
        this.firstSeenAt = firstSeenAt
      }
    }

    localStorage.removeItem('requestId')
    localStorage.removeItem('visitorId')
    localStorage.removeItem('firstSeenAt')
    localStorage.removeItem('isBot')
  }
}
